<script setup lang="ts">
import { FAKE_CATEGORY_SLUGS } from '~/constants/page-constants'

const emit = defineEmits<{
  searchBarVisibilityChanged: [isShowing: boolean]
}>()

const route = useRoute()
const searchQuery = ref(route.query.q || '')
const isSearchVisible = ref(false)
const isSearchFocused = ref(false)
const searchInputRef = ref<HTMLInputElement>()

const search = async (): Promise<void> => {
  try {
    const trimmedQuery =
    typeof searchQuery.value === 'string'
      ? searchQuery.value.trim()
      : searchQuery.value

    if (!trimmedQuery) {
      // for empty search - go to all apps without filters
      await navigateTo({
        path: getCategoryUrl(FAKE_CATEGORY_SLUGS.allApps),
      })
      return
    }

    const newQuery = {
      q: trimmedQuery,
    }

    await navigateTo({
      path: '/search',
      query: newQuery,
    })
  } finally {
    hideSearchBar()
  }
}

const showSearchBar = (): void => {
  isSearchVisible.value = true
  useDisableBodyScroll(true)
  emit('searchBarVisibilityChanged', true)
  setTimeout(() => {
    searchInputRef.value?.focus()
  }, 100)
}

const hideSearchBar = (): void => {
  isSearchVisible.value = false
  useDisableBodyScroll(false)
  emit('searchBarVisibilityChanged', false)
}
</script>

<template>
  <div class="app-searchbar">
    <form
      :class="[
        'app-searchbar__form',
        {
          '--show': isSearchVisible,
          '--focus': isSearchFocused,
        },
      ]"
      role="search"
      autocomplete="off"
      @submit.prevent="search"
    >
      <label>
        <span class="sr-only">Search</span>
        <input
          id="search"
          ref="searchInputRef"
          v-model="searchQuery"
          class="app-searchbar__input"
          placeholder="Search for an app by name"
          type="search"
          name="search"
          data-test="search"
          autocomplete="off"
          @focus="isSearchFocused = true"
          @blur="isSearchFocused = false"
        >
      </label>
      <UiButton
        class="app-searchbar__button"
        button-type="submit"
        data-test="search-submit"
      >
        <span class="sr-only">Search</span>
        <Icon
          class="app-searchbar__button-icon"
          aria-hidden="true"
          name="fa6-solid:magnifying-glass"
        />
      </UiButton>
    </form>

    <UiButton
      class="app-searchbar__button-icon-mb"
      type="icon"
      @click="showSearchBar"
    >
      <span class="sr-only">Open search</span>
      <Icon
        class="app-searchbar__button-icon"
        aria-hidden="true"
        name="fa6-solid:magnifying-glass"
      />
    </UiButton>
    <UiBackdrop
      :show="isSearchVisible"
      @click="hideSearchBar"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.app-searchbar {
  // increase specificity by nesting in .app-searchbar (SSR)
  .app-searchbar__button-icon-mb {
    display: block;

    @include min-width('md') {
      display: none;
    }
  }
}

.app-searchbar__form {
  display: block;
  border-radius: var(--app-border-radius-large);
  background-color: var(--app-color-sendcloud-white);
  border: 2px solid transparent;

  @include max-width('md') {
    display: none;
  }

  &.--focus {
    border-color: var(--app-color-sendcloud-blue);
  }

  &.--show {
    @include max-width('md') {
      display: flex;
      align-items: center;
      position: absolute;
      z-index: 1;
      left: var(--app-spacer-gutter);
      right: var(--app-spacer-gutter);

      label {
        flex: 1;
      }

      input {
        width: 100%;
      }
    }
  }

  input {
    padding: var(--app-spacer-12) var(--app-spacer-20);
    border: 0;
    background-color: transparent;

    &:focus,
    &:focus-visible {
      outline: transparent;
    }

    @include min-width('md') {
      min-width: 400px;
    }
  }

  .app-searchbar__button {
    @extend %font-17-17-bold;

    padding: var(--app-spacer-14);
    margin-right: var(--app-spacer-14);
  }
}

.app-searchbar__button-icon {
  color: var(--app-text-color-primary);
}
</style>
